import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Lightbulb,
  Users,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Mail,
  Settings,
} from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';



const appEnv = import.meta.env.VITE_APP_ENV ?? 'development'
const isStagingOrDev = ['staging', 'development'].includes(appEnv)


interface MenuChild {
  id: string;
  label: string;
  path: string;
}

interface MenuItemLink {
  type: 'link';
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  roles?: string[];
}

interface MenuItemGroup {
  type: 'group';
  id: string;
  label: string;
  icon: React.ReactNode;
  children: MenuChild[];
  roles?: string[];
}

type SidebarItem = MenuItemLink | MenuItemGroup;

interface AdminSidebarProps {
  mobileMenuOpen?: boolean;
  onCloseMobileMenu?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const sidebarItems: SidebarItem[] = [
  {
    type: 'link',
    id: 'dashboard',
    label: 'Dashboard',
    icon: <LayoutDashboard className="w-5 h-5" />,
    path: '/admin/dashboard',
    roles: ['super_admin'],
  },
  {
    type: 'group',
    id: 'spark-activities-group',
    label: 'Play Activities',
    icon: <Sparkles className="w-5 h-5" />,
    roles: ['super_admin'],
    children: [
      { id: 'activities-overview', label: 'Overview', path: '/admin/activities/overview' },
      { id: 'activities-manage', label: 'Manage', path: '/admin/activities' },
      { id: 'spark-activities-reference', label: 'Reference', path: '/admin/spark-activities' },
    ],
  },
  {
    type: 'link',
    id: 'early-access',
    label: 'Early Access',
    icon: <Mail className="w-5 h-5" />,
    path: '/admin/early-access',
    roles: ['super_admin'],
  },
  {
    type: 'link',
    id: 'expert-tips',
    label: 'Expert Tips',
    icon: <Lightbulb className="w-5 h-5" />,
    path: '/admin/expert-tips',
  },
  {
    type: 'group',
    id: 'users-group',
    label: 'Users',
    icon: <Users className="w-5 h-5" />,
    roles: ['super_admin'],
    children: [
      { id: 'users', label: 'Users', path: '/admin/users' },
      { id: 'profiles', label: 'Profiles', path: '/admin/expert-profiles' },
      { id: 'clear-onboarding', label: 'Clear Onboarding', path: '/admin/users/clear-onboarding' },
    ],
  },
  {
    type: 'group',
    id: 'settings-group',
    label: 'Settings',
    icon: <Settings className="w-5 h-5" />,
    roles: ['super_admin'],
    children: [
      { id: 'communication-stages', label: 'Communication Stages', path: '/admin/settings/communication-stages' },
      { id: 'domains', label: 'Communication Pyramid', path: '/admin/settings/domains' },
      { id: 'domain-skills', label: 'Domain Skills', path: '/admin/settings/domain-skills' },
      { id: 'child-preferences', label: 'Child Preferences', path: '/admin/settings/child-preferences' },
      { id: 'play-types', label: 'Play types', path: '/admin/settings/play-types' },
    ],
  },
];

/**
 * Fixed left sidebar with navigation menu
 */
export default function AdminSidebar({ mobileMenuOpen, onCloseMobileMenu, collapsed = false, onToggleCollapse }: AdminSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAdminAuth();

  // Filter sidebar items by role
  const visibleItems = useMemo(
    () =>
      sidebarItems.filter((item) => {
        if (!item.roles) return true;
        return user ? item.roles.includes(user.role) : false;
      }),
    [user]
  );

  const isActive = (path: string) => {
    if (path === '/admin/spark-activities') {
      // Don't mark Manage as active when on the overview page
      return (
        location.pathname === '/admin/spark-activities' ||
        (location.pathname.startsWith('/admin/spark-activities/') &&
          !location.pathname.startsWith('/admin/spark-activities/overview'))
      );
    }
    if (path === '/admin/activities') {
      // Don't mark Manage as active when on the overview page
      return (
        location.pathname === '/admin/activities' ||
        (location.pathname.startsWith('/admin/activities/') &&
          !location.pathname.startsWith('/admin/activities/overview'))
      );
    }
    if (path === '/admin/users') {
      // Don't mark Users as active on Clear Onboarding
      return (
        location.pathname === '/admin/users' ||
        (location.pathname.startsWith('/admin/users/') &&
          !location.pathname.startsWith('/admin/users/clear-onboarding'))
      );
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isGroupActive = (children: MenuChild[]): boolean => {
    return children.some(child => isActive(child.path));
  };

  const getInitialOpenGroups = (): Set<string> => {
    const open = new Set<string>();
    for (const item of visibleItems) {
      if (item.type === 'group') {
        if (item.children.some(child => isActive(child.path))) {
          open.add(item.id);
        }
      }
    }
    return open;
  };

  const [openGroups, setOpenGroups] = useState<Set<string>>(() => getInitialOpenGroups());

  useEffect(() => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const item of visibleItems) {
        if (item.type === 'group') {
          const hasActiveChild = item.children.some(
            child => location.pathname === child.path || location.pathname.startsWith(child.path + '/')
          );
          if (hasActiveChild && !next.has(item.id)) {
            next.add(item.id);
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [location.pathname, visibleItems]);

  const toggleGroup = (groupId: string) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  return (
    <aside
      className={`fixed left-0 top-0 h-screen bg-pine-card z-40 transition-all duration-300 font-body ${collapsed ? 'w-16' : 'w-64'
        } ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
    >
      {/* Logo/Brand Header */}
      <div className="h-16 flex items-center justify-between px-4 bg-pine-pine-deep border-b border-pine-pine/30">
        {!collapsed && (
          <span className="text-white font-display text-pine-heading">
            Play Moments
          </span>
        )}
        {isStagingOrDev ? <p className='text-white font-display'>(Dev)</p> : null}
        <button
          onClick={onToggleCollapse}
          className="hidden lg:block text-white/70 hover:text-white transition-colors p-1 hover:bg-pine-hover-item rounded"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <ChevronLeft className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Navigation Menu */}
      <nav className="mt-4 overflow-y-auto h-[calc(100vh-4rem)]">
        {visibleItems.map((item) => {
          if (item.type === 'link') {
            return (
              <button
                key={item.id}
                onClick={() => {
                  navigate(item.path);
                  onCloseMobileMenu?.();
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-all ${isActive(item.path)
                  ? 'bg-pine-active-item text-pine-pine border-l-4 border-pine-pine'
                  : 'text-pine-ink-soft hover:bg-pine-hover-item border-l-4 border-transparent'
                  }`}
              >
                <span className="flex-shrink-0">{item.icon}</span>
                {!collapsed && (
                  <span className="flex-1 text-left font-medium">{item.label}</span>
                )}
              </button>
            );
          }

          // Group item
          const isOpen = openGroups.has(item.id);
          const groupActive = isGroupActive(item.children);

          // In collapsed mode, navigate to first child on click
          if (collapsed) {
            return (
              <button
                key={item.id}
                onClick={() => {
                  navigate(item.children[0].path);
                  onCloseMobileMenu?.();
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-all ${groupActive
                  ? 'bg-pine-active-item text-pine-pine border-l-4 border-pine-pine'
                  : 'text-pine-ink-soft hover:bg-pine-hover-item border-l-4 border-transparent'
                  }`}
              >
                <span className="flex-shrink-0">{item.icon}</span>
              </button>
            );
          }

          // Expanded mode with submenu
          return (
            <div key={item.id}>
              <button
                onClick={() => toggleGroup(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-all ${groupActive
                  ? 'text-pine-pine border-l-4 border-pine-pine'
                  : 'text-pine-ink-soft hover:bg-pine-hover-item border-l-4 border-transparent'
                  }`}
              >
                <span className="flex-shrink-0">{item.icon}</span>
                <span className="flex-1 text-left font-medium">{item.label}</span>
                <motion.span
                  animate={{ rotate: isOpen ? 90 : 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  <ChevronRight className="w-4 h-4" />
                </motion.span>
              </button>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    {item.children.map((child) => (
                      <button
                        key={child.id}
                        onClick={() => {
                          navigate(child.path);
                          onCloseMobileMenu?.();
                        }}
                        className={`w-full flex items-center gap-3 pl-12 pr-4 py-2.5 text-sm transition-all ${isActive(child.path)
                          ? 'text-pine-pine font-semibold hover:bg-pine-hover-item'
                          : 'text-pine-ink-soft hover:bg-pine-hover-item'
                          }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isActive(child.path) ? 'bg-pine-pine' : 'bg-pine-line'
                            }`}
                        />
                        <span className="text-left">{child.label}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
